import { useEffect } from "react";

/**
 * Hook to dynamically set page meta tags for SEO.
 * Updates: <title>, meta description, OpenGraph, Twitter, canonical, JSON-LD.
 * Google's crawler executes JS, so dynamic meta is indexed.
 */
const setOrCreateMeta = (selector, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  } else {
    Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  }
  return el;
};

const setOrCreateLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
  return el;
};

const setOrCreateLdJson = (id, data) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
  return el;
};

export default function usePageMeta({
  title,
  description,
  canonical,
  image,
  type = "website",
  ldJson,
} = {}) {
  useEffect(() => {
    if (title) document.title = title;
    if (description) {
      setOrCreateMeta('meta[name="description"]', { name: "description", content: description });
    }
    if (canonical) setOrCreateLink("canonical", canonical);

    // OpenGraph
    if (title) setOrCreateMeta('meta[property="og:title"]', { property: "og:title", content: title });
    if (description) setOrCreateMeta('meta[property="og:description"]', { property: "og:description", content: description });
    if (canonical) setOrCreateMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
    setOrCreateMeta('meta[property="og:type"]', { property: "og:type", content: type });
    setOrCreateMeta('meta[property="og:site_name"]', { property: "og:site_name", content: "GT Strategy" });
    setOrCreateMeta('meta[property="og:locale"]', { property: "og:locale", content: "pt_BR" });
    if (image) setOrCreateMeta('meta[property="og:image"]', { property: "og:image", content: image });

    // Twitter
    setOrCreateMeta('meta[name="twitter:card"]', { name: "twitter:card", content: image ? "summary_large_image" : "summary" });
    if (title) setOrCreateMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    if (description) setOrCreateMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    if (image) setOrCreateMeta('meta[name="twitter:image"]', { name: "twitter:image", content: image });

    // JSON-LD structured data
    if (ldJson) setOrCreateLdJson("page-jsonld", ldJson);
  }, [title, description, canonical, image, type, JSON.stringify(ldJson || null)]);
}
